import { useContext, useState, Fragment } from 'react'
import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import Collapse from '@mui/material/Collapse'
import { Stack, SxProps, Theme, Box, Divider, useMediaQuery } from '@mui/material'
import { ContextType, SidebarContext } from '../../context/sideBarContext'
import { useImpersonation, clearCookie } from '../../context/impersonationContext'
import { t } from 'i18next'
import { useNavigate, Link } from 'react-router-dom'
import { ItemHasChildrenType, ItemType } from '../../types'
import { ActiveEnterpriseContext } from '../../context/activeEnterpriseContext'
import { ActiveRegionContext } from '../../context/activeRegionContext'
import { hasAccess, hasChildren, synthesizeMemberFromNonMemberCompany } from '../../utils'
import { authorization, itemsSubBar } from '../../constants'
import OpenCloseArrowIcon from '../../icons/openCloseArrowIcon'
import { useHandleSession } from './header/dropDownMenu'
import AvatarIcon from '../../icons/avatarIcon'
import ArrowRightIcon from '../../icons/arrowRightIcon'
import { CartContext } from '../../context/cartContext'
import { Member } from '../../types/api/user'
import { none } from 'ramda'

// Define interface for ContentItemSubMenu props
interface ContentItemSubMenuProps {
  label: string;
  region: string;
  active?: boolean;
}

// Content item for Mon Espace dropdown
const ContentItemSubMenu = ({ label, region, active = false }: ContentItemSubMenuProps) => {
  const isSmallMobile = useMediaQuery('(max-width: 480px)')
  
  return (
    <>
      <Box mr='10px' height='100%' minWidth='12px'>
        {active && <ArrowRightIcon />}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ 
          fontSize: isSmallMobile ? '12px' : '14px', 
          lineHeight: isSmallMobile ? '16px' : '18px',
          fontWeight: '500'
        }}>
          {label}
        </span>
        <span style={{ 
          fontSize: isSmallMobile ? '10px' : '12px',
          lineHeight: isSmallMobile ? '14px' : '16px',
          color: '#666',
          marginTop: '2px'
        }}>
          {region}
        </span>
      </Box>
    </>
  )
}

interface PropsItemComponent {
  handleClick?: () => void
  children: React.ReactNode
  hasSubItems?: boolean
  sx?: SxProps<Theme>
}

// Mobile-optimized Item component with larger touch targets
const Item = ({ handleClick, children, sx, hasSubItems = true }: PropsItemComponent) => {
  const isSmallMobile = useMediaQuery('(max-width: 480px)')
  const isMobile = useMediaQuery('(max-width: 800px)')
  
  return (
    <Typography
      component='li'
      sx={{
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        padding: isSmallMobile ? '10px 0' : '12px 0',  // Responsive padding
        fontSize: isSmallMobile ? '14px' : isMobile ? '16px' : '18px', // Responsive font size
        lineHeight: isSmallMobile ? '18px' : '20px',   // Responsive line height
        ...(hasSubItems ? {} : { borderBottom: '1px solid #DADCDD', pb: '15px' }),
        ...sx,
      }}
      onClick={handleClick}
    >
      {children}
    </Typography>
  )
}

const styleBeforeSelectedItem = {
  position: 'absolute',
  content: '""',
  width: '8px',
  height: '8px',
  backgroundColor: 'secondary.main',
  left: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  cursor: 'pointer',
}

const stylesSelectedItem = {
  position: 'relative',
  paddingLeft: '16px',
  '&:before': {
    ...styleBeforeSelectedItem,
  },
}

const stylesSelectedSubItem = {
  position: 'relative',
  '&:before': {
    ...styleBeforeSelectedItem,
    left: '-16px',
  },
}

interface PropsListItems {
  item: ItemHasChildrenType
}

// We don't need to persist open/closed state for mobile
// as we want the menu to close when navigating away
const ListItems = ({ item }: PropsListItems) => {
  const { selectedItemSidebar } = useContext(SidebarContext) as ContextType
  const [open, setOpen] = useState(false) // Default closed in mobile
  const isSmallMobile = useMediaQuery('(max-width: 480px)')
  const isMobile = useMediaQuery('(max-width: 800px)')
  
  const handleToogle = () => {
    setOpen((open) => !open)
  }
  const { activeEnterprise } = useContext(ActiveEnterpriseContext)

  return (
    <List
      component='nav'
      aria-labelledby='nested-list-subheader'
      sx={{
        borderBottom: '1px solid #DADCDD',
        pb: '5px',
        pt: '0px',
        mx: '12px',
        boxSizing: 'border-box',
      }}
    >
      <Item handleClick={handleToogle}>
        <span>{t(`sideBar.${item.label}`)}</span>
        <OpenCloseArrowIcon open={open} sx={{ mr: '10px' }} />
      </Item>
      <Collapse
        in={open}
        timeout='auto'
        unmountOnExit
        sx={{
          pl: '16px',
          width: '100%'
        }}
      >
        {item.children
          .filter((item) => {
            // "Documents" réservé aux vraies entreprises membres - voir
            // Member.is_real_member / synthesizeMemberFromNonMemberCompany
            if (item.item === 'entreprise.documents' && activeEnterprise.is_real_member === false) {
              return false
            }
            return hasAccess(authorization[item.mainRoute]?.read ?? null, activeEnterprise)
          })
          .map((subItem) => (
            <Typography
              key={subItem.item}
              variant='h6'
              component='li'
              sx={{
                mt: '10px',
                mb: '10px',
                py: isSmallMobile ? '6px' : '8px',
                '& a': {
                  textDecoration: 'none',
                  color: 'primary.main',
                  display: 'block',
                  width: '100%',
                  fontSize: isSmallMobile ? '14px' : isMobile ? '16px' : '18px',
                  lineHeight: isSmallMobile ? '18px' : '20px',
                },
                ...(selectedItemSidebar === subItem.item ? stylesSelectedSubItem : {}),
              }}
            >
              <Link to={subItem.mainRoute}>{t(`sideBar.${subItem.label}`)}</Link>
            </Typography>
          ))}
      </Collapse>
    </List>
  )
}

const MobileListSideBar = () => {
  const { activeEnterprise } = useContext(ActiveEnterpriseContext)
  const { activeRegion } = useContext(ActiveRegionContext)
  const { selectedItemSidebar } = useContext(SidebarContext) as ContextType
  const { setCart } = useContext(CartContext)
  const { canImpersonate, isImpersonating } = useImpersonation()
  const navigate = useNavigate()
  const [monEspaceOpen, setMonEspaceOpen] = useState(false)
  const isSmallMobile = useMediaQuery('(max-width: 480px)')

  const hasMoreThanOneChild = (item: ItemType | ItemHasChildrenType): item is ItemHasChildrenType =>
    hasChildren(item) &&
    item.children.filter((item) =>
      hasAccess(authorization[item.mainRoute]?.read ?? null, activeEnterprise),
    ).length > 1

  const { session, handleUpdateActiveEnterprise, handleUpdateActiveRegion, handleUpdateCart, updateSession } = useHandleSession()

  const handleLogout = async () => {
    // Clear impersonation cookie if currently impersonating
    if (isImpersonating) {
      clearCookie('id_token')
    }
    
    await updateSession(null)
    localStorage.clear()
    window.location.href = `${process.env.REACT_APP_ACQ_SITE as string}/logout`
  }

  const handleContactClick = () => {
    window.location.href = `${process.env.REACT_APP_JOIN_US}?regionid=${activeRegion}`
  }

  const toggleMonEspace = () => {
    setMonEspaceOpen(!monEspaceOpen)
  }

  return (
    <Box sx={{ width: '100%', px: 0, mt: '0px' }}>
      <Stack sx={{ width: '100%', boxSizing: 'border-box', paddingX: 0}}>
        {(session?.is_member || (session?.non_member_companies?.length ?? 0) > 0) && (
          <>
            <Item
              sx={{
                fontWeight: '500',
                backgroundColor: '#DADCDD',
                pl: '12px',
                pr: '22px',
              }}
              handleClick={toggleMonEspace}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', height: '36px' }}>
                <AvatarIcon />
                <Typography 
                  variant='h6' 
                  component='span' 
                  sx={{ 
                    ml: '6px', 
                    mr: '10px', 
                    fontSize: isSmallMobile ? '14px' : '16px',
                    lineHeight: isSmallMobile ? '18px' : '20px'
                  }}
                >
                  Mon espace
                </Typography>
              </Box>
              <OpenCloseArrowIcon open={monEspaceOpen} />
            </Item>

            <Collapse in={monEspaceOpen} timeout="auto" unmountOnExit>
            <Stack spacing='8px' sx={{ width: '100%', backgroundColor: '#DADCDD', pb: '20px' }}>
                {session?.is_member && session?.members?.map((member: Member) => (
                  <Typography
                    key={member.crm_id}
                    variant='smallText'
                    component='li'
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: isSmallMobile ? '40px' : '44px',
                      backgroundColor: 'primary.lighter',
                      pl: '19px',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      fontSize: isSmallMobile ? '12px' : '14px',
                      lineHeight: isSmallMobile ? '16px' : '18px',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'white',
                      },
                      '&:hover svg path': {
                        fill: '#fff',
                      },
                    }}
                    onClick={() => {
                      handleUpdateActiveEnterprise(member)
                      handleUpdateActiveRegion(member.region?.wp_id.fr)
                      handleUpdateCart(member.cart_item)
                      setMonEspaceOpen(false) // Close dropdown after selection
                    }}
                  >
                    <ContentItemSubMenu
                      label={member.legal_name}
                      region={member.region?.name}
                      active={member.crm_id === activeEnterprise.crm_id}
                    />
                  </Typography>
                ))}
                {session?.non_member_companies?.map((company) => {
                  const synthesizedMember = synthesizeMemberFromNonMemberCompany(company)
                  return (
                    <Typography
                      key={company.crm_id}
                      variant='smallText'
                      component='li'
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: isSmallMobile ? '40px' : '44px',
                        backgroundColor: 'primary.lighter',
                        pl: '19px',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        fontSize: isSmallMobile ? '12px' : '14px',
                        lineHeight: isSmallMobile ? '16px' : '18px',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          color: 'white',
                        },
                        '&:hover svg path': {
                          fill: '#fff',
                        },
                      }}
                      onClick={() => {
                        handleUpdateActiveEnterprise(synthesizedMember)
                        handleUpdateActiveRegion(company.region?.wp_id.fr ?? null)
                        handleUpdateCart(null)
                        setMonEspaceOpen(false)
                      }}
                    >
                      <ContentItemSubMenu
                        label={company.legal_name}
                        region={company.region?.name ?? ''}
                        active={company.crm_id === activeEnterprise.crm_id}
                      />
                    </Typography>
                  )
                })}
              </Stack>
            </Collapse>
          </>
        )}

        <Divider sx={{  mb: '8px', border: 'none' }} />

        {/* Regular sidebar items */}
        {itemsSubBar
          .filter((item) => {
            if (item.item === 'personification' && (!canImpersonate() || isImpersonating)) {
              return false
            }
            // "Avantages Membres" réservé aux vraies entreprises membres -
            // voir Member.is_real_member / synthesizeMemberFromNonMemberCompany
            if (item.item === 'memberBenefits' && activeEnterprise.is_real_member === false) {
              return false
            }
            return hasAccess(item.roles, activeEnterprise)
          })
          .map((item) => (
            <Fragment key={item.item}>
              {hasMoreThanOneChild(item) ? (
                <ListItems item={item} />
              ) : (
                <Item
                  sx={{
                    ...(item.item === selectedItemSidebar ? stylesSelectedItem : {}),
                    borderBottom: '1px solid #DADCDD',
                    marginX: '12px!important',
                  }}
                  hasSubItems={false}
                  handleClick={() => navigate(item.mainRoute)}
                >
                  <span>{t(`sideBar.${item.label}`)}</span>
                </Item>
              )}
            </Fragment>
          ))}

        {/* Nous Joindre button */}
        <Item
          sx={{
            color: 'primary.main',
            borderBottom: 'none',
            marginX: '12px',
          }}
          hasSubItems={false}
          handleClick={handleContactClick}
        >
          <span>Nous joindre</span>
        </Item>

        {/* Logout button */}
        <Item
          sx={{
            backgroundColor: 'secondary.main',
            color: '#FFFFFF',
            '&:hover': 'none',
            textTransform: 'uppercase',
            textAlign: 'center',
            justifyContent: 'center',
            fontWeight: '500',
            padding: '12px 12px',
            mt: '8px',
            mx: '12px',
          }}
          hasSubItems={false}
          handleClick={handleLogout}
        >
          <span>Déconnexion</span>
        </Item>
      </Stack>
    </Box>
  )
}

export default MobileListSideBar
